import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import {useCallback, useEffect, useState} from "react";
import Grid from "@mui/material/Grid";
import CheckIcon from "@mui/icons-material/Check";
import {Link, useParams, useSearchParams} from "react-router";
import {TapeProcessingView} from "../../component/processing/TapeProcessingView";
import {OrderDocuments} from "../Admin/Job/JobManagement/JobDocuments/jobDocuments";
import {useAuth} from "../AuthProvider/AuthProvider";
import {JobSummary} from "../../component/details/jobSummary";
import {fetchJob} from "../../utils/apiCalls";
import {VerticalLinearStepper} from "../../component/tracking/VerticalLinearStepper";
import {PROCESSING_TAB, TAB_PARAM} from "../../component/widgets/MediaMetrics/mediaFilterNavigation";

// Maps status → last COMPLETED step (✓ mark on horizontal stepper)
// Step 1=Order Placed, 2=Shipping, 3=Processing (audit+ingestion)
const statusToCompletedStep = {
    "preparing_for_shipment": 1,
    "courier_en_route": 1,
    "shipment_collected": 1,
    "out_for_delivery": 1,
    "verified_and_completed": 2,
    "audit_started": 2,
    "audit_complete": 2,
    "ingestion_started": 2,
    "ingestion_complete": 3
};

// Maps status → sub-content panel to show (which vertical section is active)
const statusToSubStep = {
    "preparing_for_shipment": 2,
    "courier_en_route": 2,
    "shipment_collected": 2,
    "out_for_delivery": 2,
    "verified_and_completed": 2,
    "audit_started": 3,
    "audit_complete": 3,
    "ingestion_started": 3,
    "ingestion_complete": 3
};

// Ordered list of stages — add/remove entries here to change the stepper
const STAGES = [
    {key: 1, label: "Job\nPlaced"},
    {key: 2, label: "Shipping", clickable: true},
    {key: 3, label: "Processing", clickable: true},
];

/** The stepper stage that shows the tape table, named because `?tab=processing` now asks for it by link. */
const PROCESSING_STEP = 3;

export const JobTracking = () => {
    const staticState = [
        {key: "ingestion_complete"},
        {key: "ingestion_started"},
        {key: "audit_complete"},
        {key: "audit_started"},
        {key: "verified_and_completed"},
        {key: "out_for_delivery"},
        {key: "shipment_collected"},
        {key: "courier_en_route"},
        {key: "preparing_for_shipment"},
    ];
    const {user, isAdmin} = useAuth();
    const cid = isAdmin ? 0 : user?.arkbridge_customer_id;

    /*
     * Whether a link asked for the tape table.
     *
     * A customer reaches this page by clicking a Media Metrics tile, which links to
     * `?tab=processing&media=<key>`. On this route the table is not behind a tab but behind the stepper's
     * Processing stage, so honouring the link means selecting that stage. The tab parameter is still the
     * thing read: one tile builds one link, and each page knows what "processing" means for its own layout.
     */
    const [searchParams] = useSearchParams();
    const wantsProcessing = searchParams.get(TAB_PARAM) === PROCESSING_TAB;

    const [hStepInFocus, setHStepInFocus] = useState(wantsProcessing ? PROCESSING_STEP : 1);
    const params = useParams();
    const [orderDetail, setOrderDetail] = useState({});
    const [processingComplete, setProcessingComplete] = useState(false);

    useEffect(() => {
        getOrderDetail();
    }, []);

    /**
     * Function used:
     * - to retrieve order details for the given order id
     * - ensure that all the order states are present in the order details object (where information is missing, blank values are added)
     * - stores the order details in the state variable orderDetail.
     * Side effects:
     * - updates the state variable orderDetail
     * - updates the state variable hStepInFocus – used to determine the active step in the horizontal stepper
     */
    const getOrderDetail = () => {
        fetchJob(params.orderId)
            .then((data) => {
                const key = "status";
                data["_order_states"] = JSON.parse(JSON.stringify(data.order_states));
                data.order_states = [...new Map(data.order_states.map((item) => [item[key], item])).values()];
                staticState.forEach((stateDescription) => {
                    let orderState = data._order_states.find((elements) => String(elements.state) === stateDescription.key);
                    if (orderState?.state) {
                        stateDescription["timestamp"] = orderState.timestamp;
                        stateDescription["message"] = orderState.message;
                        stateDescription["status"] = String(orderState.state);
                    } else {
                        stateDescription["timestamp"] = "";
                        stateDescription["message"] = "";
                        stateDescription["status"] = String(stateDescription.key);
                    }
                });
                data.order_states = staticState;
                setOrderDetail(data);
                // The link wins over the status: this lands after the first paint, so without the guard an
                // order whose status says "shipping" would pull the reader off the table they clicked to see.
                if (!wantsProcessing) setHStepInFocus(getHActiveStep(data));
            })
            .catch((error) => console.log(error));
    };

    function getHActiveStep(_orderDetail) {
        return statusToSubStep[_orderDetail.status.toLowerCase()] ?? 1;
    }

    const [tabInFocus, setTabInFocus] = useState("1");
    const handleTabChange = (event, newTabValue) => {
        setTabInFocus(newTabValue);
    };

    const getCompletedStepIndex = useCallback(() => {
        if (processingComplete) return 3;
        return statusToCompletedStep[orderDetail?.status?.toLowerCase()] ?? 1;
    }, [orderDetail, processingComplete]);

    const updateHStepInFocus = useCallback((step) => () => setHStepInFocus(step), []);

    /**
     * OrderProgressStepper — clean data-driven horizontal stepper.
     * Each stage shows: circle (✓ if done, number if pending/active) + label below.
     * Stages are connected by horizontal lines. Consistent spacing throughout.
     */
    const OrderProgressStepper = () => {
        const completedStep = getCompletedStepIndex();
        return (
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 3}}>
                {STAGES.map((stage, idx) => {
                    const done = completedStep >= stage.key;
                    const active = completedStep === stage.key - 1;
                    // clickable if step is completed OR is the current active step
                    const clickable = stage.clickable && (completedStep >= stage.key || active);
                    return (
                        <Box key={stage.key} sx={{display: 'flex', alignItems: 'center', flex: idx < STAGES.length - 1 ? 1 : 'none'}}>
                            {/* Step circle + label */}
                            <Box
                                sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: clickable ? 'pointer' : 'default', minWidth: 70}}
                                onClick={clickable ? updateHStepInFocus(stage.key) : undefined}
                            >
                                <Box sx={{
                                    width: 35, height: 35, borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: done ? 'rgba(255,197,64,0.13)' : 'transparent',
                                    border: active ? '2px solid #FFC540' : done ? '2px solid #FFC540' : '2px solid #5F5F5F',
                                    color: done ? '#FFC540' : active ? '#FFC540' : '#5F5F5F',
                                    fontFamily: 'Poppins', fontSize: 14, fontWeight: 600,
                                }}>
                                    {done ? <CheckIcon sx={{fontSize: 18, color: '#FFC540'}}/> : stage.key}
                                </Box>
                                <Box sx={{mt: 0.5, textAlign: 'center', whiteSpace: 'pre-line', fontSize: 12, fontFamily: 'Poppins', color: done || active ? '#FFFFFF' : '#5F5F5F'}}>
                                    {stage.label}
                                </Box>
                            </Box>
                            {/* Connector line */}
                            {idx < STAGES.length - 1 && (
                                <Box sx={{flex: 1, height: '1px', background: done ? '#FFC540' : '#5F5F5F', mx: 1, mb: 2.5}}/>
                            )}
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const trackingTabPanelContent = useCallback(() => {
        const hStepToComponent = {
            2: <VerticalLinearStepper orderDetail={orderDetail}/>,
            3: <TapeProcessingView orderDetail={orderDetail} cid={cid} onDataLoaded={({entriesCount, completedCount}) => setProcessingComplete(entriesCount > 0 && completedCount === entriesCount)}/>,
        };

        return (
            <Grid container direction="column">
                <OrderProgressStepper/>
                <Grid item>
                    {hStepToComponent[hStepInFocus]}
                </Grid>
            </Grid>
        );
    }, [hStepInFocus, orderDetail]);

    /**
     * Function to render the content of the Details tab panel. It renders only if the tab in focus is the Details tab.
     * @type {(function(): *)|*}
     */
    const detailsTabPanelContent = useCallback(() => {
        return <JobSummary orderDetail={orderDetail}/>;
    }, [orderDetail]);

    /**
     * Function to render the content of the Uploads tab panel. It renders only if the tab in focus is the Uploads tab.
     * @type {(function(): *)|*}
     */
    const documentsTabPanelContent = useCallback(() => {
        return <OrderDocuments orderDetail={orderDetail}/>;
    }, [orderDetail]);

    const tabs = {
        1: {label: "Tracking", content: trackingTabPanelContent},
        2: {label: "Details", content: detailsTabPanelContent},
        3: {label: "Documents", content: documentsTabPanelContent},
    };

    /**
     * Function to get the tab list component.
     * @returns {Element}
     */
    const getTabList = useCallback(() => {
        const tabSX = {
            "&.MuiTab-root": {
                color: "#5F5F5F",
                fontFamily: "Poppins",
                fontSize: "14px",
                fontWeight: 700,
                textTransform: "capitalize",
            }
        };

        return (
            <Box sx={{borderBottom: 1, borderColor: "divider"}}>
                <TabList
                    sx={{
                        "& .MuiTabs-scroller": {
                            background: "linear-gradient(180deg, #EEAB10 0%, #FFC133 100%)",
                        }, "& .MuiTabs-indicator": {
                            background: "#3A3E3F", mb: "10px",
                        },
                    }}
                    onChange={handleTabChange}
                    aria-label="lab API tabs example"
                    className="tab-bg">
                    {Object.keys(tabs).map((key) => [
                        <Tab
                            sx={tabSX}
                            label={tabs[key].label}
                            value={key}
                        />
                    ])}
                </TabList>
            </Box>
        );
    }, []);

    /**
     * Function to get the tab panels component.
     * @returns {React.JSX.Element[][]}
     */
    const getTabPanels = useCallback(() => {
        return Object.keys(tabs).map((key) => [
            <TabPanel sx={tabs[key].sx ? tabs[key].sx : {}} value={key}>
                {tabs[key].content()}
            </TabPanel>
        ]);
    }, [trackingTabPanelContent, detailsTabPanelContent, documentsTabPanelContent]);

    const getBreadcrumb = useCallback(() => {
        return (
            <div className="breadcrumb">
                <img src="/assets/img/cart.svg" alt=""/>
                <img src="/assets/img/arrow_left.svg" alt=""/>
                <Link to="/CustomerJobs" style={{textDecoration: "none"}}>
                    <small className="grey-color">Your Jobs</small>
                </Link>
                <img src="/assets/img/arrow_left.svg" alt=""/>
                <small className="grey-color">{orderDetail["order_number"]}</small>
            </div>
        );
    }, [orderDetail]);

    return (
        <>
            <Box p={4}>
                {getBreadcrumb()}
                <div className="heading">
                    <h1>{orderDetail["order_number"]}</h1>
                </div>
                <Box mb={2}>
                    <hr/>
                </Box>
                <div className="tab-section">
                    <TabContext value={tabInFocus}>
                        {getTabList()}
                        {getTabPanels()}
                    </TabContext>
                </div>
            </Box>
        </>);
};

